// src/screens/BrandsAdminScreen.tsx

import React, { useEffect, useState } from "react";
import { getBrands, postBrand, editBrand } from "../../services/api/getAll";
import { useForm } from "react-hook-form";
import FacebookIcon from "@mui/icons-material/Facebook";
import InstagramIcon from "@mui/icons-material/Instagram";
import AppleIcon from "@mui/icons-material/Apple";
import AndroidIcon from "@mui/icons-material/Android";
import EditIcon from "@mui/icons-material/Edit";
import TikTokIcon from "@mui/icons-material/MusicNote";

const inputStyle = {
  width: "100%",
  padding: "12px",
  marginBottom: "16px",
  background: "#f5f5f5",
  border: "1px solid #ccc",
  borderRadius: "8px",
  color: "#000",
  fontSize: "16px",
};

const labelStyle = {
  display: "block",
  marginBottom: "6px",
  color: "#555",
  fontWeight: "bold",
};

const BrandsAdminScreen = () => {
  const [brand, setBrand] = useState(null);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(false);
  const { register, handleSubmit, setValue } = useForm();

  const fetchBrand = async () => {
    try {
      setLoading(true);
      const data = await getBrands();
      const latest = data[data.length - 1];
      setBrand(latest);
      setValue("facebookAccount", latest.facebookAccount);
      setValue("instagramAccount", latest.instagramAccount);
      setValue("tikTokAccount", latest.tikTokAccount);
      setValue("appleStoreLink", latest.appleStoreLink);
      setValue("googlePlayStoreLink", latest.googlePlayStoreLink);
    } catch (err) {
      console.error("Failed to fetch brand", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBrand();
  }, []);

  const onSubmit = async (data) => {
    try {
      setLoading(true);
      if (brand?.id) {
        await editBrand(brand.id, { ...data, id: brand.id, title: "", description: "" });
      } else {
        await postBrand({ ...data, title: "", description: "" });
      }
      setEditing(false);
      await fetchBrand();
    } catch (err) {
      console.error("Failed to save brand", err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      style={{
        maxWidth: "900px",
        margin: "40px auto",
        padding: "32px",
        backgroundColor: "#fff",
        boxShadow: "0 4px 8px rgba(0, 0, 0, 0.05)",
        borderRadius: "16px",
        color: "#000",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          marginBottom: "24px",
        }}
      >
        <h2 style={{ color: "#444", margin: 0 }}>App Brand Settings</h2>
        {!editing && (
          <button
            onClick={() => setEditing(true)}
            style={{
              background: "none",
              border: "none",
              color: "#888",
              fontSize: "18px",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
            }}
          >
            <EditIcon />
          </button>
        )}
      </div>

      {loading ? (
        <div style={{ textAlign: "center", padding: "40px 0", color: "#888" }}>
          Loading...
        </div>
      ) : (
        <form onSubmit={handleSubmit(onSubmit)}>
          <div style={{ display: "flex", flexWrap: "wrap", gap: "24px" }}>
            <div style={{ flex: "1 1 45%" }}>
              <label style={labelStyle}>Facebook Link</label>
              <input
                type="text"
                {...register("facebookAccount")}
                disabled={!editing}
                style={inputStyle}
              />
            </div>

            <div style={{ flex: "1 1 45%" }}>
              <label style={labelStyle}>Instagram Link</label>
              <input
                type="text"
                {...register("instagramAccount")}
                disabled={!editing}
                style={inputStyle}
              />
            </div>

            <div style={{ flex: "1 1 45%" }}>
              <label style={labelStyle}>TikTok Link</label>
              <input
                type="text"
                {...register("tikTokAccount")}
                disabled={!editing}
                style={inputStyle}
              />
            </div>

            <div style={{ flex: "1 1 45%" }}>
              <label style={labelStyle}>Apple Store Link</label>
              <input
                type="text"
                {...register("appleStoreLink")}
                disabled={!editing}
                style={inputStyle}
              />
            </div>

            <div style={{ flex: "1 1 45%" }}>
              <label style={labelStyle}>Google Play Link</label>
              <input
                type="text"
                {...register("googlePlayStoreLink")}
                disabled={!editing}
                style={inputStyle}
              />
            </div>
          </div>

          <div style={{ marginTop: "24px" }}>
            {editing && (
              <button
                type="submit"
                style={{
                  width: "100%",
                  padding: "14px",
                  backgroundColor: "#e0e0e0",
                  border: "none",
                  borderRadius: "8px",
                  color: "#000",
                  fontWeight: "bold",
                  fontSize: "16px",
                  cursor: "pointer",
                }}
              >
                {loading ? "Saving..." : "Save Changes"}
              </button>
            )}
          </div>
        </form>
      )}

      {!editing && brand && (
        <div style={{ marginTop: "48px" }}>
          <h3 style={{ color: "#444", marginBottom: "16px" }}>Quick Preview</h3>
          <div
            style={{
              background: "#f9f9f9",
              padding: "24px",
              borderRadius: "12px",
              lineHeight: "2",
              border: "1px solid #ddd",
              color: "#222",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <FacebookIcon style={{ color: "#3b5998" }} />
              {brand.facebookAccount}
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <InstagramIcon style={{ color: "#E1306C" }} />
              {brand.instagramAccount}
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <TikTokIcon style={{ color: "#69C9D0" }} />
              {brand.tikTokAccount}
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <AppleIcon style={{ color: "#A3AAAE" }} />
              {brand.appleStoreLink}
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <AndroidIcon style={{ color: "#3DDC84" }} />
              {brand.googlePlayStoreLink}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BrandsAdminScreen;
